/**
 * Shared building blocks for the Grading Setup page sections.
 * Pure presentational components — no API, no state of their own except trivial ones.
 */
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

export const uid = () => crypto.randomUUID();

export function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function StepCard({ step, title, subtitle, children, testId, badge }) {
  return (
    <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-brand-purple font-heading font-bold mb-0.5">{step}</p>
          <h2 className="font-heading text-lg sm:text-xl font-semibold text-foreground">{title}</h2>
          {subtitle && <p className="text-sm text-brand-inkMuted mt-0.5">{subtitle}</p>}
        </div>
        {badge}
      </div>
      {children}
    </section>
  );
}

export function ToggleRow({ label, description, checked, onChange, testId, flat = false }) {
  return (
    <label className={`${flat ? "" : "mt-3"} flex items-start gap-3 cursor-pointer p-2 rounded-xl hover:bg-brand-purpleLight/30`}>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
      <div>
        <p className="font-heading text-sm font-semibold text-foreground">{label}</p>
        {description && <p className="text-xs text-brand-inkMuted">{description}</p>}
      </div>
    </label>
  );
}

export function GeneratePeriods({ onGenerate }) {
  const [n, setN] = useState(4);
  return (
    <div className="flex items-end gap-2">
      <Field label="GRADING TERMS">
        <Input type="number" min={1} max={12} value={n} onChange={(e) => setN(Number(e.target.value) || 1)}
          className="rounded-xl h-11 w-32" data-testid="gs-generate-count" />
      </Field>
      <Button onClick={() => onGenerate(n)} className="rounded-2xl h-11 bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid="gs-generate-btn">
        Generate
      </Button>
    </div>
  );
}

export function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/* Option lists, exported so each section can reuse them */
export const SCALE_OPTIONS = [
  { value: "percentage", label: "Percentage-Based System" },
  { value: "letter", label: "Letter Grade System" },
  { value: "gpa", label: "GPA System" },
  { value: "custom", label: "Custom System" },
];

export const CATEGORY_LABEL_OPTIONS = [
  { value: "subjects", label: "Subjects" },
  { value: "learning_areas", label: "Learning Areas" },
  { value: "competencies", label: "Competencies" },
  { value: "custom", label: "Custom" },
];

export const GROUPING_LABEL_OPTIONS = [
  { value: "description", label: "Description" },
  { value: "domain", label: "Domain" },
  { value: "area", label: "Area" },
  { value: "cluster", label: "Cluster" },
  { value: "category_group", label: "Category Group" },
  { value: "custom", label: "Custom" },
];

export const EMPTY_SETUP_STATE = {
  settings: {
    grading_mode: "calculated",
    direct_assessment_level: "category",
    grading_scale_type: "percentage",
    allow_grading_term_comments: false,
    custom_description_enabled: true,
    custom_percentage_range_enabled: false,
    report_card_signatories: [],
  },
  scale_ranges: [],
  periods: [],
  category_settings: {
    category_label: "subjects",
    custom_label: "",
    grouping_enabled: false,
    grouping_label: "domain",
    custom_grouping_label: "",
  },
  groups: [],
  categories: [],
};
